"use client"

import { useMemo } from "react"
import { motion } from "framer-motion"
import type { DailyEntry } from "@/lib/db/schema"
import { getZoneById } from "@/lib/zones"
import {
  getCurrentWeek,
  getCzechWeekdayShort,
  formatDayNumber,
  isTodayDate,
  groupEntriesByDay,
  calculateDailyAverage,
  averageToZoneId,
  toLocalDateString,
  getWeekStart,
} from "@/lib/date-utils"
import { getContrastColor, getMutedContrastColor } from "@/lib/color-utils"
import { cn } from "@/lib/utils"
import {
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  addDays,
  subDays,
} from "date-fns"

interface DesktopMonthViewProps {
  entries: DailyEntry[]
  currentDate?: Date
  onDayClick?: (date: Date) => void
}

/**
 * DesktopMonthView - Full month calendar grid optimized for desktop
 *
 * Features:
 * - Full month grid layout with proper week alignment
 * - Monday as first day of week (Czech locale)
 * - Responsive cell sizing (56px tablet, 64px desktop)
 * - Multiple entry visualization with dots and average colors
 * - Streak highlighting
 * - Click handlers for day selection
 */
export function DesktopMonthView({
  entries,
  currentDate = new Date(),
  onDayClick,
}: DesktopMonthViewProps) {
  // Get all days to display in the month grid
  const monthDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate)
    const monthEnd = endOfMonth(currentDate)

    // Get the Monday of the week containing the first day of the month
    const gridStart = getWeekStart(monthStart)

    // Calculate how many days we need to show (usually 5-6 weeks)
    const daysFromStart = Math.ceil((monthEnd.getTime() - gridStart.getTime()) / (1000 * 60 * 60 * 24))
    const weeksNeeded = Math.ceil((daysFromStart + 1) / 7)
    const totalDays = weeksNeeded * 7

    // Generate all days for the grid
    const days: Date[] = []
    for (let i = 0; i < totalDays; i++) {
      days.push(addDays(gridStart, i))
    }

    return days
  }, [currentDate])

  // Group entries by date for efficient lookups
  const entriesByDate = useMemo(() => groupEntriesByDay(entries), [entries])

  // Get weekday headers
  const weekdays = useMemo(() => {
    const week = getCurrentWeek(currentDate)
    return week.map((day) => getCzechWeekdayShort(day))
  }, [currentDate])

  return (
    <div className="w-full space-y-2">
      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-1 md:gap-2">
        {weekdays.map((day, index) => (
          <div
            key={index}
            className="text-center text-xs md:text-sm font-semibold text-muted-foreground py-2"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-2 md:gap-3">
        {monthDays.map((date, index) => {
          const dateStr = toLocalDateString(date)
          const dayEntries = entriesByDate.get(dateStr) || []
          const isToday = isTodayDate(date)
          const isCurrentMonth = isSameMonth(date, currentDate)
          const entryCount = dayEntries.length

          // Calculate average zone for background color
          const averageZone = calculateDailyAverage(dayEntries)
          const averageZoneId = averageToZoneId(averageZone)
          const zone = averageZoneId ? getZoneById(averageZoneId) : null

          return (
            <motion.button
              key={dateStr}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.01 }}
              onClick={() => onDayClick?.(date)}
              className={cn(
                "relative w-full aspect-square rounded-lg",
                "flex flex-col items-center justify-center",
                "transition-all duration-200",
                "hover:scale-105 active:scale-95",
                "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1",
                isToday && "ring-2 ring-primary ring-offset-1",
                !isCurrentMonth && "opacity-40",
                entryCount === 0 && "bg-muted/20 hover:bg-muted/40",
                entryCount > 0 && "shadow-sm hover:shadow-md"
              )}
              style={{
                backgroundColor: zone?.color
                  ? zone.color.replace(/oklch\(([^)]+)\)/, 'oklch($1 / 0.85)')
                  : undefined,
                opacity: !isCurrentMonth ? 0.3 : entryCount === 0 ? 0.5 : 1,
              }}
              aria-label={`${formatDayNumber(date)}. ${getCzechWeekdayShort(date)}, ${entryCount} ${
                entryCount === 1 ? "záznam" : "záznamy"
              }`}
            >
              {/* Day number */}
              <span
                className={cn(
                  "text-sm md:text-base font-semibold mb-1",
                  !zone && "text-muted-foreground",
                  isToday && "font-bold",
                  !isCurrentMonth && "text-muted-foreground/50"
                )}
                style={{
                  color: zone?.color ? getContrastColor(zone.color) : undefined
                }}
              >
                {formatDayNumber(date)}
              </span>

              {/* Entry count dots */}
              {entryCount > 0 && (
                <div className="flex gap-0.5 items-center justify-center">
                  {Array.from({ length: Math.min(entryCount, 3) }).map((_, i) => (
                    <div
                      key={i}
                      className="w-1 h-1 rounded-full"
                      style={{
                        backgroundColor: zone?.color
                          ? getMutedContrastColor(zone.color)
                          : undefined
                      }}
                    />
                  ))}
                </div>
              )}
            </motion.button>
          )
        })}
      </div>
    </div>
  )
}
